import type { Rng } from '../rng';
import { MYSTERY_IDS, type ClueSlot, type ClueTemplate, type MysteryId } from './types';
import { cluesForSlot } from './definitions';
import { SKELETON, type SkeletonBeat } from './skeleton';
import type { CollectedClue } from './ledger';

/**
 * Erzeugt aus einem Seed einen vollstaendigen Kriminalfall.
 *
 * Die beiden Eigenschaften, an denen alles haengt, entstehen hier **durch
 * Konstruktion** und nicht durch nachtraegliches Pruefen und Neuwuerfeln:
 *
 *  1. **Mehrdeutigkeit bis Akt III.** Die weichen Beats der Akte I und II
 *     werden reihum auf Wahrheit und die beiden falschen Faehrten verteilt.
 *     Bei acht Beats und drei Quellen ergibt das 3/3/2 — die Wahrheit ist
 *     danach nie die alleinige Spitze der weichen Beweislage.
 *
 *  2. **Eindeutige Loesbarkeit am Ende.** Alle harten Beats und der gesamte
 *     Akt III gehoeren der Wahrheit. Harte Hinweise wiegen dreifach, also
 *     fuehrt die Wahrheit am Ende mit deutlichem Abstand.
 *
 * Der Fuzztest ueber tausende Seeds prueft beides nach.
 */

export interface CaseBeat {
  readonly beat: SkeletonBeat;
  readonly clue: ClueTemplate;
  readonly isTruth: boolean;
}

export interface MysteryCase {
  readonly seed: string;
  readonly truth: MysteryId;
  /** Die beiden falschen Faehrten dieses Durchlaufs. */
  readonly decoys: readonly MysteryId[];
  readonly beats: readonly CaseBeat[];
}

const DECOY_COUNT = 2;

/**
 * Zieht Hinweise einer Aufloesung fuer einen Slot ohne Wiederholung.
 * Ohne diesen Vorrat koennte derselbe Fundtext zweimal im Fall auftauchen.
 */
class CluePool {
  private readonly pools = new Map<string, ClueTemplate[]>();

  constructor(private readonly rng: Rng) {}

  take(mystery: MysteryId, slot: ClueSlot): ClueTemplate {
    const key = `${mystery}:${slot}`;
    let pool = this.pools.get(key);

    if (pool === undefined || pool.length === 0) {
      // Beim ersten Zugriff mischen; bei Erschoepfung neu auffuellen. Letzteres
      // sollte nach den Variantenzahlen nie eintreten und wird vom Fuzztest
      // ueberwacht — als Rueckfallebene ist es trotzdem besser als ein Absturz.
      pool = this.rng.shuffle(cluesForSlot(mystery, slot));
      this.pools.set(key, pool);
    }

    const clue = pool.pop();
    if (clue === undefined) {
      throw new Error(`Keine Hinweisvorlage für ${mystery}/${slot}`);
    }
    return clue;
  }
}

export function generateCase(rng: Rng): MysteryCase {
  const caseRng = rng.derive('case');

  const truth = caseRng.pick(MYSTERY_IDS);
  const decoys = caseRng.sample(
    MYSTERY_IDS.filter((id) => id !== truth),
    DECOY_COUNT,
  );

  // Die Reihenfolge im Zyklus wird gemischt. Landet die Wahrheit an letzter
  // Stelle, bekommt sie sogar einen Beat weniger als die falschen Faehrten —
  // in keinem Fall mehr als beide.
  const cycle = caseRng.shuffle([truth, ...decoys]);

  const pool = new CluePool(caseRng);
  let softIndex = 0;

  const beats: CaseBeat[] = SKELETON.map((beat) => {
    let source: MysteryId;

    if (beat.truthOnly) {
      source = truth;
    } else {
      source = cycle[softIndex % cycle.length] as MysteryId;
      softIndex++;
    }

    return {
      beat,
      clue: pool.take(source, beat.slot),
      isTruth: source === truth,
    };
  });

  return { seed: caseRng.seed, truth, decoys, beats };
}

/** Wandelt einen Fall-Beat in einen Eintrag der Beweislage. */
export function toCollectedClue(caseBeat: CaseBeat, sourceLabel: string): CollectedClue {
  return {
    templateId: caseBeat.clue.id,
    mystery: caseBeat.clue.mystery,
    slot: caseBeat.clue.slot,
    textKey: caseBeat.clue.textKey,
    weight: caseBeat.clue.weight,
    beatIndex: caseBeat.beat.index,
    act: caseBeat.beat.act,
    sourceLabel,
  };
}

/** Alle Aufloesungen, die in diesem Fall ueberhaupt vorkommen. */
export function candidatesIn(mysteryCase: MysteryCase): MysteryId[] {
  return [mysteryCase.truth, ...mysteryCase.decoys];
}
